// client/src/pages/Register.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(""); // ✅ NEW

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!name || !email || !password) {
      setError("Please fill all fields.");
      return;
    }

    setLoading(true);
    const res = await register({ name, email, password });
    setLoading(false);

    if (!res.ok) {
      setError(res.message || "Registration failed");
    } else {
      setSuccess(
        "Account created successfully. Please login with your email and password."
      );

      // Redirect after short delay so user sees message
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#05040f] via-[#07071a] to-[#05040f] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md rounded-[30px] bg-white/5 border border-white/10 backdrop-blur-2xl p-8 md:p-10 shadow-[0_40px_120px_rgba(0,0,0,0.7)]"
      >
        <div className="flex flex-col items-center mb-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-3 mb-3"
          >
            <img
              src="/logo.png"
              alt="smsorbit"
              className="w-10 h-10 rounded-xl"
            />
            <div className="text-left">
              <div className="font-bold text-lg">smsorbit</div>
              <div className="text-xs text-gray-300">
                Smart SMM Automation Panel
              </div>
            </div>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-center">
            Create Account
          </h1>
          <p className="text-xs md:text-sm text-gray-300 mt-1 text-center">
            Get instant access to services, orders &amp; API.
          </p>
        </div>

        {/* ERROR MESSAGE */}
        {error && (
          <div className="mb-4 rounded-xl bg-red-500/10 border border-red-500/50 px-4 py-2 text-sm text-red-100">
            {error}
          </div>
        )}

        {/* SUCCESS MESSAGE */}
        {success && (
          <div className="mb-4 rounded-xl bg-green-500/10 border border-green-500/50 px-4 py-2 text-sm text-green-100">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-300 mb-1">
              Full Name
            </label>
            <input
              type="text"
              className="w-full px-4 py-2.5 rounded-xl bg-black/40 border border-white/15 text-sm outline-none focus:border-purple-400"
              placeholder="Enter Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-300 mb-1">Email</label>
            <input
              type="email"
              className="w-full px-4 py-2.5 rounded-xl bg-black/40 border border-white/15 text-sm outline-none focus:border-purple-400"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs text-gray-300 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                className="w-full px-4 py-2.5 pr-16 rounded-xl bg-black/40 border border-white/15 text-sm outline-none focus:border-purple-400"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="absolute inset-y-0 right-3 flex items-center text-xs text-gray-300 hover:text-white"
              >
                {showPass ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed font-semibold text-sm md:text-base shadow-lg shadow-purple-500/40"
          >
            {loading ? "Creating account..." : "Create Account"}
          </button>
        </form>

        <p className="mt-5 text-xs text-gray-300 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-purple-300 font-semibold">
            Login
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
